import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import api, { imageUrl, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import ImageUploadField from "@/components/admin/ImageUploadField";
import BannerResponsivePreview from "@/components/admin/BannerResponsivePreview";
import {
  computePreviewContainerClass,
  pickPreviewImage,
  getFocalObjectPosition,
  hasMobileOverride as hasMobileOverrideRule,
} from "@/lib/bannerRenderer";

const empty = {
  image_url: "",
  mobile_image_url: "",
  link_url: "",
  title: "",
  subtitle: "",
  badge: "",
  button_text: "",
  button_link: "",
  text_alignment: "left",
  overlay_strength: 60,
  focal_point: "center",
  order: 0,
  is_active: true,
};

// Copy Admin — mengomunikasikan bahwa satu banner sudah cukup. Mobile override
// hanya untuk kasus lanjutan.
const PRIMARY_NOTES = [
  "Ukuran maksimal file: 2 MB",
  "Rekomendasi: rasio sekitar 2:1 (mis. 1600 × 800 atau 1920 × 960 px) untuk hasil terbaik di desktop dan mobile",
  "Satu banner dipakai otomatis di desktop, tablet, dan mobile — tetap landscape (bukan syarat wajib; banner lama tetap tampil)",
  "Format: PNG, JPG, JPEG, atau WEBP",
];
const MOBILE_OVERRIDE_NOTES = [
  "Opsional — hanya jika ingin desain khusus untuk tampilan mobile",
  "Rekomendasi: 900 × 1200 px (rasio 3:4) — portrait",
  "Jika kosong, sistem otomatis pakai Banner Utama",
];

const FOCAL_OPTIONS = [
  { value: "left", label: "Kiri" },
  { value: "center", label: "Tengah" },
  { value: "right", label: "Kanan" },
];

function BannerForm({ initial, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [uploadingPrimary, setUploadingPrimary] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(!!(initial && initial.mobile_image_url));
  const uploading = uploadingPrimary || uploadingMobile;
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_url) { toast.error("Banner Utama wajib diunggah."); return; }
    try {
      const p = {
        ...form,
        order: Number(form.order) || 0,
        overlay_strength: Math.min(Math.max(Number(form.overlay_strength) || 0, 0), 100),
        focal_point: FOCAL_OPTIONS.some((o) => o.value === form.focal_point) ? form.focal_point : "center",
      };
      if (initial?.id) await api.put(`/banners/${initial.id}`, p);
      else await api.post("/banners", p);
      toast.success("Banner tersimpan");
      onSaved?.();
    } catch (err) { notifyError(err); }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      {/* ==================== BANNER UTAMA ==================== */}
      <div className="space-y-2">
        <ImageUploadField
          label="Banner Utama"
          required
          value={form.image_url}
          onChange={(v) => upd("image_url", v)}
          notes={PRIMARY_NOTES}
          testId="banner-upload-primary"
          onUploadingChange={setUploadingPrimary}
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Focal Point (untuk mobile fallback)</Label>
            <select
              value={form.focal_point || "center"}
              onChange={(e) => upd("focal_point", e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="banner-form-focal-point"
            >
              {FOCAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-[#5C5C5C] mt-1">
              Menentukan sisi gambar yang tetap terlihat saat Banner Utama di-crop otomatis di mobile.
            </p>
          </div>
          <div>
            <Label>Overlay (0-100)</Label>
            <Input type="number" min={0} max={100} value={form.overlay_strength ?? 60}
              onChange={(e) => upd("overlay_strength", e.target.value)}
              data-testid="banner-form-overlay" />
          </div>
        </div>
      </div>

      {/* ==================== LIVE RESPONSIVE PREVIEW ==================== */}
      {form.image_url && (
        <BannerResponsivePreview
          image_url={form.image_url}
          mobile_image_url={form.mobile_image_url}
          focal_point={form.focal_point}
          title={form.title}
          testId="banner-form-preview"
        />
      )}

      {/* ==================== ADVANCED / OPTIONAL ==================== */}
      <div className="rounded-xl border border-dashed border-[#E5E5E5] bg-[#FAF9F6]">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-left text-sm font-bold"
          data-testid="banner-form-advanced-toggle"
          aria-expanded={advancedOpen}
        >
          <span>Opsional / Lanjutan · Banner Mobile Khusus</span>
          {advancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {advancedOpen && (
          <div className="px-3 pb-3">
            <p className="text-[11px] text-[#5C5C5C] mb-2">
              Gunakan hanya jika Anda ingin desain khusus untuk tampilan mobile.
              Jika kosong, Banner Utama otomatis dipakai di mobile.
            </p>
            <ImageUploadField
              label="Banner Mobile Khusus (opsional)"
              value={form.mobile_image_url || ""}
              onChange={(v) => upd("mobile_image_url", v)}
              notes={MOBILE_OVERRIDE_NOTES}
              testId="banner-upload-mobile"
              onUploadingChange={setUploadingMobile}
            />
          </div>
        )}
      </div>

      {/* ==================== KONTEN ==================== */}
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Judul</Label><Input value={form.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid="banner-form-title" /></div>
        <div><Label>Sub Judul</Label><Input value={form.subtitle || ""} onChange={(e) => upd("subtitle", e.target.value)} data-testid="banner-form-subtitle" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Badge (opsional)</Label><Input value={form.badge || ""} onChange={(e) => upd("badge", e.target.value)} placeholder="Promo" data-testid="banner-form-badge" /></div>
        <div><Label>Teks Tombol</Label><Input value={form.button_text || ""} onChange={(e) => upd("button_text", e.target.value)} placeholder="Belanja Sekarang" data-testid="banner-form-button-text" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Link Tombol</Label><Input value={form.button_link || ""} onChange={(e) => upd("button_link", e.target.value)} placeholder="/katalog" data-testid="banner-form-button-link" /></div>
        <div><Label>Link URL (fallback)</Label><Input value={form.link_url || ""} onChange={(e) => upd("link_url", e.target.value)} placeholder="/katalog" data-testid="banner-form-link-url" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Posisi Teks</Label>
          <select value={form.text_alignment || "left"} onChange={(e) => upd("text_alignment", e.target.value)}
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="banner-form-text-alignment">
            <option value="left">Kiri</option>
            <option value="center">Tengah</option>
            <option value="right">Kanan</option>
          </select>
        </div>
        <div><Label>Urutan</Label><Input type="number" value={form.order} onChange={(e) => upd("order", e.target.value)} data-testid="banner-form-order" /></div>
      </div>
      <Label className="flex items-center gap-2">
        <Switch checked={form.is_active} onCheckedChange={(v) => upd("is_active", v)} data-testid="banner-form-active-switch" /> Aktif
      </Label>
      <DialogFooter>
        <Button type="submit" disabled={uploading} className="brand-btn-primary rounded-xl" data-testid="banner-form-submit">
          {uploading ? "Mengunggah gambar…" : "Simpan"}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** Card thumbnail — memakai renderer SAMA dengan storefront (WYSIWYG). */
function BannerCard({ b, onEdit, onRemove }) {
  const desktopSrc = pickPreviewImage({ breakpoint: "desktop", image_url: b.image_url, mobile_image_url: b.mobile_image_url });
  const mobileSrc = pickPreviewImage({ breakpoint: "mobile", image_url: b.image_url, mobile_image_url: b.mobile_image_url });
  const hasMobileOverride = hasMobileOverrideRule({ image_url: b.image_url, mobile_image_url: b.mobile_image_url });
  const focalCls = getFocalObjectPosition(b.focal_point);
  const desktopCls = computePreviewContainerClass({ breakpoint: "desktop", hasMobileOverride });
  const mobileCls = computePreviewContainerClass({ breakpoint: "mobile", hasMobileOverride });
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid={`banner-card-${b.id}`}>
      <div className="flex bg-[#FAF9F6]">
        <div className={`${desktopCls} flex-1 overflow-hidden`} data-testid={`banner-card-desktop-preview-${b.id}`}>
          {desktopSrc && <img src={imageUrl(desktopSrc)} alt={`${b.title || "Banner"} — desktop`} className={`w-full h-full object-cover ${focalCls}`} />}
        </div>
        <div className={`${mobileCls} w-20 shrink-0 border-l border-[#E5E5E5] overflow-hidden`} data-testid={`banner-card-mobile-preview-${b.id}`}>
          {mobileSrc && <img src={imageUrl(mobileSrc)} alt={`${b.title || "Banner"} — mobile`} className={`w-full h-full object-cover ${focalCls}`} />}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-bold truncate">{b.title || "Tanpa Judul"}</div>
            <div className="text-xs text-[#5C5C5C] truncate">{b.subtitle}</div>
          </div>
          <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded shrink-0 ${b.is_active ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-red-100 text-red-700"}`}>{b.is_active ? "Aktif" : "Nonaktif"}</span>
        </div>
        <div className="text-xs text-[#5C5C5C] mt-1 flex items-center gap-2 flex-wrap">
          <span>Urutan: {b.order}</span>
          <span>·</span>
          <span className="truncate">Link: {b.link_url || b.button_link || "-"}</span>
          {hasMobileOverride && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#1A1A1A] bg-[#EDE9F6] rounded-full px-2 py-0.5">
              Mobile Override
            </span>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={onEdit} className="rounded-lg" data-testid={`banner-card-edit-${b.id}`}><Pencil size={12} /></Button>
          <Button size="sm" variant="outline" onClick={onRemove} className="rounded-lg text-[#DC2F02]" data-testid={`banner-card-remove-${b.id}`}><Trash2 size={12} /></Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/banners", { params: { active_only: false } }).then((r) => setBanners(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Hapus banner?")) return;
    await api.delete(`/banners/${id}`);
    load();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black">Banner Promo</h1>
          <p className="text-xs text-[#5C5C5C] mt-1">
            Cukup upload satu Banner Utama — sistem otomatis render responsif di desktop, tablet, dan mobile.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><Button className="brand-btn-primary rounded-xl" data-tour="banner-page"><Plus size={14} /> Tambah Banner</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Banner</DialogTitle></DialogHeader>
            <BannerForm key={editing?.id || "new"} initial={editing} onSaved={() => { load(); setOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {banners.map((b) => (
          <BannerCard
            key={b.id}
            b={b}
            onEdit={() => { setEditing(b); setOpen(true); }}
            onRemove={() => remove(b.id)}
          />
        ))}
        {banners.length === 0 && <div className="md:col-span-2 text-center py-12 text-[#5C5C5C] bg-white rounded-2xl border border-[#E5E5E5]">Belum ada banner</div>}
      </div>
    </AdminLayout>
  );
}
